// Tachograph drivers: everyone found in the files (card holders and cards seen in vehicle units),
// each with their drivers' hours checks worked out from all their files.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Customer, customers as customersApi, tacho, TachoDriver as Driver, TachoDriverDay } from '../api';
import { DataGrid } from '../components/DataGrid';
import { Link, NavLink } from '../components/Links';
import { SearchSelect } from '../components/SearchSelect';
import { TachoDays } from '../components/TachoDays';
import { dayOnly, daysFrom } from '../format';
import { KIND_LABEL, periodText } from './TachoFiles';

/** Files / Drivers switch at the top of the tachograph pages. */
export function TachoTabs() {
  return (
    <nav className="tacho-tabs" aria-label="Tachograph">
      <NavLink to="/tacho" end>Files</NavLink>
      <NavLink to="/tacho/drivers">Drivers</NavLink>
    </nav>
  );
}

// The company must download each driver card at least every 28 days.
const CARD_DOWNLOAD_DAYS = 28;
const overdue = (d: Pick<Driver, 'lastCardDownload'>) => !d.lastCardDownload || -daysFrom(d.lastCardDownload) > CARD_DOWNLOAD_DAYS;

export function TachoDrivers() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const owner = params.get('customer') ?? '';
  const [typed, setTyped] = useState(q);
  const [rows, setRows] = useState<Driver[] | null>(null);
  const [custs, setCusts] = useState<Customer[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      if (typed.trim() !== q) setParams((p) => { typed.trim() ? p.set('q', typed.trim()) : p.delete('q'); return p; }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [typed, q, setParams]);
  useEffect(() => { customersApi.list().then((r) => setCusts(r.customers)).catch(() => {}); }, []);
  useEffect(() => {
    tacho.drivers(q, owner === 'none' ? 'none' : owner ? Number(owner) : null).then((r) => { setRows(r.drivers); setError(''); }).catch((e) => setError(e.message));
  }, [q, owner]);
  const customerOptions = useMemo(() => [{ value: 'none', label: 'No customer yet' }, ...custs.map((c) => ({ value: String(c.id), label: c.name }))], [custs]);

  return (
    <div className="page wide">
      <div className="page-head"><h1>Tachograph</h1></div>
      <TachoTabs />
      {error && <p className="error" role="alert">{error}</p>}
      <div className="toolbar">
        <label className="sr-only" htmlFor="drv-search">Search</label>
        <input id="drv-search" type="search" placeholder="Search name, card number or customer…" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <label className="sr-only" htmlFor="drv-customer">Customer</label>
        <SearchSelect id="drv-customer" value={owner} options={customerOptions} emptyLabel="All customers"
          onChange={(v) => setParams((p) => { v ? p.set('customer', v) : p.delete('customer'); return p; }, { replace: true })} />
      </div>
      {rows && (
        <DataGrid id="tacho-drivers" rows={rows} rowKey={(d) => d.id} noun={['driver', 'drivers']} href={(d) => `/tacho/drivers/${d.id}`}
          defaultSort={{ key: 'name', dir: 1 }} empty={q || owner ? 'No drivers match.' : 'No drivers yet. They appear when tachograph files are added.'} columns={[
            { key: 'name', label: 'Driver', always: true, value: (d) => d.name, cell: (d) => <Link to={`/tacho/drivers/${d.id}`}><strong>{d.name}</strong></Link> },
            { key: 'card', label: 'Card', value: (d) => d.cardNumber, cell: (d) => <>{d.cardNumber}{d.cardNation && <span className="muted"> · {d.cardNation}</span>}</>, nowrap: true },
            { key: 'customer', label: 'Customer', value: (d) => d.customerName ?? '', cell: (d) => d.customerName ?? <span className="muted">–</span> },
            { key: 'period', label: 'Data', value: (d) => d.dataTo ?? '', csv: (d) => periodText(d.dataFrom, d.dataTo), cell: (d) => periodText(d.dataFrom, d.dataTo), nowrap: true },
            { key: 'infringements', label: 'Infringements', align: 'right', value: (d) => d.infringements,
              cell: (d) => (d.infringements ? <span className="tacho-count bad">{d.infringements}</span> : <span className="muted">0</span>) },
            { key: 'card-download', label: 'Card downloaded', value: (d) => d.lastCardDownload ?? '', sortBy: (d) => Date.parse(d.lastCardDownload ?? '') || 0,
              csv: (d) => (d.lastCardDownload ? dayOnly(d.lastCardDownload) : ''),
              cell: (d) => <span className={overdue(d) ? 'warn' : undefined}>{d.lastCardDownload ? dayOnly(d.lastCardDownload) : 'never'}{overdue(d) ? ' – overdue' : ''}</span>, nowrap: true },
            { key: 'files', label: 'Files', align: 'right', value: (d) => d.files ?? 0, hidden: true },
          ]} />
      )}
    </div>
  );
}

export function TachoDriverPage() {
  const id = Number(useParams().id);
  const [data, setData] = useState<Awaited<ReturnType<typeof tacho.driver>> | null>(null);
  const [custs, setCusts] = useState<Customer[]>([]);
  const [error, setError] = useState('');
  const load = useCallback(() => { tacho.driver(id).then((r) => { setData(r); setError(''); }).catch((e) => setError((e as Error).message)); }, [id]);
  useEffect(load, [load]);
  useEffect(() => { customersApi.list().then((r) => setCusts(r.customers)).catch(() => {}); }, []);
  const dayInfringements = useCallback((d: TachoDriverDay) => d.infringements, []);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of data?.days ?? []) for (const i of d.infringements) m.set(i.title, (m.get(i.title) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [data]);

  if (!data) return <div className="page"><p><Link to="/tacho/drivers">← Drivers</Link></p>{error ? <p className="error">{error}</p> : <p className="muted">Working out the checks…</p>}</div>;
  const d = data.driver;
  return (
    <div className="page">
      <p><Link to="/tacho/drivers">← Drivers</Link></p>
      <div className="page-head">
        <div>
          <h1>{d.name}</h1>
          <p className="muted small">Driver card {d.cardNumber}{d.cardNation ? ` · ${d.cardNation}` : ''}</p>
        </div>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <section className="card form" aria-label="Summary">
        <h2>Drivers' hours</h2>
        <dl className="facts">
          <dt>Data from files</dt><dd>{periodText(data.from?.slice(0, 10) ?? null, data.to?.slice(0, 10) ?? null)}</dd>
          <dt>Infringements</dt>
          <dd>{counts.length ? counts.map(([t, n]) => <div key={t}><span className="tacho-count bad">{n}</span> {t}</div>) : 'None found'}</dd>
          <dt>Card last downloaded</dt>
          <dd className={overdue(d) ? 'warn' : undefined}>{d.lastCardDownload ? dayOnly(d.lastCardDownload) : 'No card download yet'}
            {overdue(d) && ` – due at least every ${CARD_DOWNLOAD_DAYS} days`}</dd>
          <dt><label htmlFor="drv-file-customer">Customer</label></dt>
          <dd>
            <SearchSelect id="drv-file-customer" value={d.customerId ? String(d.customerId) : ''} emptyLabel="– none yet –"
              options={custs.map((c) => ({ value: String(c.id), label: c.name }))}
              onChange={(v) => void tacho.fileDriver(d.id, v ? Number(v) : null).then(load).catch((e) => setError((e as Error).message))} />
          </dd>
        </dl>
        <p className="muted small">
          Checked against EU driving and rest rules (Regulation 561/2006): breaks, daily, weekly and two-week driving, daily and weekly rest.
          Not yet taken into account: ferry or train crossings, emergencies, the "return home" extension, making up reduced weekly rests,
          the international exception for two reduced weekly rests in a row, and working time rules. Rest rules are only judged where the files cover the whole period.
          This is an aid for the operator, not a legal decision.
        </p>
      </section>
      <section className="card" aria-label="Files">
        <h2>Files</h2>
        <ul className="tacho-file-list">
          {data.files.map((f) => (
            <li key={f.id}>
              <Link to={`/tacho/${f.id}`}>{f.role === 'holder' ? 'Own card' : `${KIND_LABEL[f.kind]} ${f.registration ?? ''}`}</Link>
              <span className="muted small"> · {periodText(f.periodFrom, f.periodTo)}{f.downloadedAt ? ` · downloaded ${dayOnly(f.downloadedAt)}` : ''}</span>
            </li>
          ))}
        </ul>
      </section>
      <TachoDays days={data.days} infringements={dayInfringements} period={periodText(data.from?.slice(0, 10) ?? null, data.to?.slice(0, 10) ?? null)} />
    </div>
  );
}
